import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Button,
    Box,
    Typography,
    Slide,
    IconButton,
    CircularProgress
} from '@mui/material';
import {
    Warning as WarningIcon,
    Close as CloseIcon,
    Payment as PaymentIcon,
    AccessTime as ClockIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { get, post } from '../services/apiService';

// Transición para el diálogo
const Transition = React.forwardRef(function Transition(props, ref) {
    return <Slide direction="down" ref={ref} {...props} />;
});

const InactiveServiceWarning = ({ open, onClose, autoCloseTime = 10000 }) => {
    const [countdown, setCountdown] = useState(autoCloseTime / 1000);
    const [progress, setProgress] = useState(100);
    const [lastInvoice, setLastInvoice] = useState(null);
    const [loadingPromise, setLoadingPromise] = useState(false);
    const [errorPromise, setErrorPromise] = useState('');
    const [successPromise, setSuccessPromise] = useState('');
    const [timerPaused, setTimerPaused] = useState(false);
    const navigate = useNavigate();

    // Cargar datos de facturas para verificar elegibilidad de prórroga
    useEffect(() => {
        if (!open) return;
        
        // Reiniciar estados al abrir
        setErrorPromise('');
        setSuccessPromise('');
        setLastInvoice(null);
        setTimerPaused(false);
        setCountdown(autoCloseTime / 1000);
        setProgress(100);

        const loadInvoiceInfo = async () => {
            try {
                const data = await get('/api/client/dashboard/stats', true);
                if (data && data.lastInvoice) {
                    setLastInvoice(data.lastInvoice);
                }
            } catch (err) {
                console.error('Error fetching invoice for warning:', err);
            }
        };
        loadInvoiceInfo();
    }, [open, autoCloseTime]);

    // Efecto para el temporizador de cierre automático
    useEffect(() => {
        if (!open || timerPaused) return;

        let timer;
        let countdownTimer;

        // Contador para el tiempo restante
        countdownTimer = setInterval(() => {
            setCountdown(prev => {
                if (prev <= 1) {
                    clearInterval(countdownTimer);
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);

        // Actualiza el progreso de la barra circular
        timer = setInterval(() => {
            setProgress(prev => {
                const newProgress = prev - (100 / (autoCloseTime / 1000)) / 10;
                if (newProgress <= 0) {
                    clearInterval(timer);
                    onClose();
                    return 0;
                }
                return newProgress;
            });
        }, 100);

        // Cierre automático después del tiempo especificado
        const autoClose = setTimeout(() => {
            onClose();
        }, autoCloseTime);

        // Limpieza
        return () => {
            clearTimeout(autoClose);
            clearInterval(timer);
            clearInterval(countdownTimer);
        };
    }, [open, onClose, autoCloseTime, timerPaused]);

    const handleGoToPayments = () => {
        onClose();
        navigate('/client/payments', { 
            state: { 
                invoiceId: lastInvoice?.id,
                autoOpenPayment: true
            } 
        });
    };

    const handleRequestPromise = async () => {
        if (!lastInvoice) return;
        setTimerPaused(true); // Detener el temporizador de cierre
        setLoadingPromise(true);
        setErrorPromise('');
        setSuccessPromise('');
        try {
            const res = await post(`/api/client/invoices/${lastInvoice.id}/promise`, {}, true);
            if (res.success) {
                setSuccessPromise('¡Prórroga autorizada con éxito! Su servicio de internet ha sido restablecido.');
                setTimeout(() => {
                    onClose();
                    window.location.reload(); // Recargar la app para refrescar el layout y el estado del servicio
                }, 2500);
            } else {
                setErrorPromise(res.message || 'Error al procesar la prórroga de pago.');
            }
        } catch (err) {
            console.error('Error requesting promise from warning:', err);
            setErrorPromise(err.message || 'Error de conexión al solicitar la prórroga.');
        } finally {
            setLoadingPromise(false);
        }
    };

    // Determinar si es elegible para prórroga
    const canRequestPromise = lastInvoice && !lastInvoice.payment_promise_date && lastInvoice.status !== 'paid';

    return (
        <Dialog
            open={open}
            TransitionComponent={Transition}
            keepMounted
            maxWidth="sm"
            fullWidth
            onClose={onClose}
            aria-describedby="inactive-service-alert-dialog"
            PaperProps={{
                sx: {
                    borderTop: '4px solid #f44336',
                    borderRadius: '8px',
                    position: 'relative',
                    overflow: 'visible'
                }
            }}
        >
            <Box
                sx={{
                    position: 'absolute',
                    top: -20,
                    left: '50%',
                    transform: 'translateX(-50%)',
                    bgcolor: '#f44336',
                    color: 'white',
                    borderRadius: '50%',
                    width: 56,
                    height: 56,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: 2
                }}
            >
                <WarningIcon sx={{ fontSize: 32 }} />
            </Box>

            <Box sx={{ position: 'absolute', top: 8, right: 8 }}>
                <IconButton
                    edge="end"
                    color="inherit"
                    onClick={onClose}
                    aria-label="close"
                >
                    <CloseIcon />
                </IconButton>
            </Box>

            <DialogTitle sx={{ textAlign: 'center', mt: 3, pt: 2 }}>
                Servicio Suspendido
            </DialogTitle>

            <DialogContent>
                {!timerPaused && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                        <Box sx={{ position: 'relative', display: 'inline-flex' }}>
                            <CircularProgress
                                variant="determinate"
                                value={progress}
                                size={40}
                                thickness={4}
                                sx={{ color: '#f44336' }}
                            />
                            <Box
                                sx={{
                                    top: 0,
                                    left: 0,
                                    bottom: 0,
                                    right: 0,
                                    position: 'absolute',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                }}
                            >
                                <Typography variant="caption" component="div" color="text.secondary">
                                    {countdown}
                                </Typography>
                            </Box>
                        </Box>
                    </Box>
                )}

                <DialogContentText id="inactive-service-alert-dialog-description" align="center">
                    Su servicio de internet se encuentra actualmente suspendido por falta de pago.
                    Por favor, realice un pago o solicite una prórroga para reactivarlo hoy mismo.
                </DialogContentText>

                {/* Sección de Prórroga Auto-gestionada */}
                {canRequestPromise && (
                    <Box sx={{ mt: 3, p: 2.5, bgcolor: '#fffde7', border: '1px solid #fbc02d', borderRadius: 2 }}>
                        <Typography variant="subtitle1" color="warning.dark" gutterBottom sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 1 }}>
                            <ClockIcon fontSize="small" />
                            ¿Necesita internet hoy?
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                            Puede solicitar una prórroga de compromiso de pago. Su internet se reactivará temporalmente por un plazo de <strong>7 días</strong> a partir de este momento.
                        </Typography>
                        
                        {errorPromise && (
                            <Typography variant="body2" color="error" sx={{ mb: 1.5, fontWeight: 'bold' }}>
                                {errorPromise}
                            </Typography>
                        )}
                        {successPromise && (
                            <Typography variant="body2" color="success.main" sx={{ mb: 1.5, fontWeight: 'bold' }}>
                                {successPromise}
                            </Typography>
                        )}

                        {!successPromise && (
                            <Button
                                variant="contained"
                                color="warning"
                                fullWidth
                                onClick={handleRequestPromise}
                                disabled={loadingPromise}
                                startIcon={loadingPromise ? <CircularProgress size={16} color="inherit" /> : null}
                                sx={{ 
                                    fontWeight: 'bold',
                                    bgcolor: 'warning.main', 
                                    color: 'warning.contrastText',
                                    '&:hover': { bgcolor: 'warning.dark' }
                                }}
                            >
                                {loadingPromise ? 'Procesando reactivación...' : 'Solicitar Prórroga de 7 días'}
                            </Button>
                        )}
                    </Box>
                )}

                {/* Nota general */}
                {!canRequestPromise && (
                    <Box sx={{ mt: 3, bgcolor: '#fff9f9', p: 2, borderRadius: 1 }}>
                        <Typography variant="subtitle2" color="error" gutterBottom>
                            Importante:
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Una vez realizado el pago, su servicio será reactivado de forma automática.
                            Si ya solicitó su prórroga para esta factura o tiene dudas, por favor contacte a soporte técnico.
                        </Typography>
                    </Box>
                )}
            </DialogContent>

            <DialogActions sx={{ justifyContent: 'center', pb: 3 }}>
                <Button
                    onClick={onClose}
                    color="inherit"
                    variant="outlined"
                    disabled={loadingPromise}
                >
                    Cerrar
                </Button>
                <Button
                    onClick={handleGoToPayments}
                    color="primary"
                    variant="contained"
                    startIcon={<PaymentIcon />}
                    disabled={loadingPromise}
                    autoFocus
                >
                    Realizar Pago
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default InactiveServiceWarning;